import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import { normalizeBlocksData } from '../../../utils/dataMapping';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmationModal from '../../common/ConfirmationModal';
import StatusTooltip from '../../common/StatusTooltip';
import HealthStatusCards from './components/HealthStatusCards';
import BlockDetailsView from './components/BlockDetailsView';
import SlotManager from './components/SlotManager';
import ImageUpload from './components/ImageUpload';
// Removed real-time hooks to reduce cloud costs - using 1-minute interval polling instead
import { getNormalizedBlockStatus } from '../../../utils/statusUtils';
import './styles/index.css';
import { FiRefreshCw, FiChevronDown, FiChevronRight, FiBattery, FiDroplet, FiRss, FiDollarSign } from 'react-icons/fi';

const BlockManagement = () => {
  const { user, userRole, franchiseAccess, operatorBlocks, isAdmin, isFranchisee, isOperator } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [sunscreenTypes, setSunscreenTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    actionType: 'delete',
    itemName: '',
    onConfirm: null
  });
  
  // Note: Removed unused state variables that were causing ESLint warnings
  // If you need tabs, tools, health filter, refill modal, or sunscreen config in the future,
  // you can add them back when they're actually implemented

  // Get initial block list (serials only) for real-time subscriptions
  const fetchInitialBlocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // Get basic block list for real-time subscriptions
      const rawBlocksData = await api.getBlocksWithStats();
      const normalizedBlocks = normalizeBlocksData(rawBlocksData);
      
      // Apply role-based filtering
      let filteredBlocks = normalizedBlocks;
      if (userRole) {
        console.log('DEBUG: Block filtering - userRole:', userRole);
        console.log('DEBUG: Block filtering - isAdmin:', isAdmin);
        console.log('DEBUG: Block filtering - isFranchisee:', isFranchisee);
        console.log('DEBUG: Block filtering - isOperator:', isOperator);
        console.log('DEBUG: Block filtering - operatorBlocks:', operatorBlocks);
        console.log('DEBUG: Block filtering - totalBlocks:', normalizedBlocks.length);
        
        if (isAdmin()) {
          // Admins see all blocks
          console.log('DEBUG: Admin user - showing all blocks');
        } else if (isFranchisee()) {
          // Franchisees see all blocks in their franchise
          const accessibleFranchiseIds = franchiseAccess.map(access => access.franchiseId);
          filteredBlocks = normalizedBlocks.filter(block => 
            accessibleFranchiseIds.includes(block.assignedFranchise)
          );
          console.log('DEBUG: Franchisee user - filtered to', filteredBlocks.length, 'blocks');
        } else if (isOperator()) {
          // Operators see only their assigned blocks
          console.log('DEBUG: Operator user - operatorBlocks:', operatorBlocks);
          filteredBlocks = normalizedBlocks.filter(block => {
            const isAssigned = operatorBlocks.includes(block.serial);
            console.log('DEBUG: Block', block.serial, 'assigned to operator:', isAssigned);
            return isAssigned;
          });
          console.log('DEBUG: Operator user - filtered to', filteredBlocks.length, 'blocks');
        }
      }

      setBlocks(filteredBlocks);
      
    } catch (err) {
      console.error('Error fetching initial blocks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userRole, isAdmin, isFranchisee, isOperator, franchiseAccess]);
  
  // OPTIMIZED: Smarter refresh function with throttling
  const refreshBlocks = useCallback(async (force = false) => {
    // OPTIMIZATION: Prevent excessive refreshes
    const now = Date.now();
    const lastRefresh = refreshBlocks.lastCall || 0;
    const timeSinceLastRefresh = now - lastRefresh;
    
    // Only allow refresh every 10 seconds unless forced
    if (!force && timeSinceLastRefresh < 10000) {
      console.log('⏸️ Refresh throttled - too soon since last refresh');
      return;
    }
    
    refreshBlocks.lastCall = now;
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      await fetchInitialBlocks();
      if (force) {
        setSuccessMessage('Block data refreshed successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error refreshing blocks:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [fetchInitialBlocks]);

  const fetchSunscreenTypes = async () => {
    try {
      // Use API service for sunscreen configuration
      const config = await api.getSunscreenConfiguration();
      if (config && config.types) {
        setSunscreenTypes(config.types);
      } else {
        // Fallback to default types
        const defaultTypes = [
          { id: 'SPF30', name: 'SPF 30', description: 'Standard protection sunscreen' },
          { id: 'SPF50', name: 'SPF 50', description: 'High protection sunscreen' },
          { id: 'SPF15', name: 'SPF 15', description: 'Light protection sunscreen' }
        ];
        setSunscreenTypes(defaultTypes);
      }
    } catch (err) {
      console.error('Error fetching sunscreen types:', err);
      // Fallback to default types on error
      const defaultTypes = [
        { id: 'SPF30', name: 'SPF 30', description: 'Standard protection sunscreen' },
        { id: 'SPF50', name: 'SPF 50', description: 'High protection sunscreen' },
        { id: 'SPF15', name: 'SPF 15', description: 'Light protection sunscreen' }
      ];
      setSunscreenTypes(defaultTypes);
    }
  };

  // Load blocks and sunscreen types on component mount
  useEffect(() => {
    // For operators, we need to wait for operatorBlocks to be loaded
    if (isOperator && isOperator() && (!operatorBlocks || operatorBlocks.length === 0)) {
      console.log('⏳ Waiting for operator blocks to load...');
      return;
    }
    
    console.log('🚀 BlockManagement mounted - loading initial data');
    console.log('User role:', userRole, 'Operator blocks:', operatorBlocks);
    fetchInitialBlocks();
    fetchSunscreenTypes();
  }, [fetchInitialBlocks, isOperator, operatorBlocks, userRole]); // Include operatorBlocks in dependencies

  // 1-minute interval polling system (replaces expensive real-time subscriptions)
  const [pollingBlocks, setPollingBlocks] = useState({});
  
  // OPTIMIZED: Reduced polling frequency and smart polling
  useEffect(() => {
    if (blocks.length === 0) return;
    
    const pollBlockStatus = async () => {
      try {
        // Only poll if user is actively viewing the dashboard
        if (document.visibilityState !== 'visible') {
          console.log('⏸️ Skipping poll - page not visible');
          return;
        }
        
        // OPTIMIZATION: Only refresh a subset of blocks if many devices
        // This prevents overwhelming the API with large deployments
        const maxBlocksPerPoll = 20;
        if (blocks.length > maxBlocksPerPoll) {
          console.log(`📊 Large deployment detected (${blocks.length} blocks), using staggered polling`);
          // In production, implement round-robin polling here
          // For now, just log and continue with full poll
        }
        
        const rawBlocksData = await api.getBlocksWithStats();
        const normalizedBlocks = normalizeBlocksData(rawBlocksData);
        
        // Convert to map for easy lookup
        const blocksMap = {};
        normalizedBlocks.forEach(block => {
          if (block.serial) {
            blocksMap[block.serial] = block;
          }
        });
        
        setPollingBlocks(blocksMap);
      } catch (err) {
        console.error('Error polling block status:', err);
      }
    };
    
    // Initial poll
    pollBlockStatus();
    
    // OPTIMIZATION: Increased interval from 60s to 2 minutes for better scaling
    // Real-time updates should come from webhooks, not polling
    const intervalId = setInterval(pollBlockStatus, 120000); // 2 minutes
    
    return () => clearInterval(intervalId);
  }, [blocks.length]);
  
  // Merge static block data with polling-based status data
  const blocksWithCanonicalStatus = blocks.map(block => {
    const pollingData = pollingBlocks[block.serial];
    if (pollingData) {
      return {
        ...block,
        ...pollingData,
        // Preserve critical static data
        location: block.location,
        assignedFranchise: block.assignedFranchise,
        // Use canonical status from server (don't recalculate client-side)
        status: pollingData.status,
        statusReason: pollingData.statusReason,
        lastHeartbeat: pollingData.lastHeartbeat,
        activeErrors: pollingData.activeErrors,
        activeWarnings: pollingData.activeWarnings,
        // Flag to indicate we have canonical status
        hasCanonicalStatus: true
      };
    }
    return block;
  });
  


  // Note: Removed unused dashboard and business stats calculations
  // These can be added back when needed for actual UI display

  // Helper functions for UI interaction
  const toggleLocation = useCallback((location) => {
    setExpandedLocations(prev => ({
      ...prev,
      [location]: !prev[location]
    }));
  }, []);

  const toggleBlock = useCallback((blockId) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  }, []);

  // Note: Removed unused utility functions (formatTemperature, formatTimestamp, getStatusTooltip)
  // These can be added back when needed for actual UI display

  const calculateDailyRevenue = useCallback((block) => {
    // Use the backend-provided daily dispense count with realistic pricing
    // The backend already calculates dispensesToday in the correct timezone
    const dailyDispenses = block.dispensesToday || 0;
    
    // Use a realistic average price per dispense based on backend data
    // Most dispenses are $0.50-$1.00, some are free (for maintenance/testing)
    const avgPricePerDispense = 0.75; // Conservative estimate
    
    return dailyDispenses * avgPricePerDispense;
  }, []);





  const handleRefillSlot = async (blockSerial, slotIndex, refillData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // OPTIMIZATION: Removed unnecessary device status check
      // Device status is already available from the blocks data
      // Only check status when needed for critical operations like payments
      
      // Step 2: Refill both slots with correct API parameters
      // Use volumeML: 'FULL' which will be converted to 5000ml by the backend
      const sunscreenName = refillData.sunscreen_type || 'Coral Isle SPF 30';
      
      // Refill slot 0
      console.log(`Refilling slot 0 with: FULL capacity of ${sunscreenName}`);
      await api.device.registerRefill(blockSerial, {
        volumeML: 'FULL',
        slotIndex: 0,
        sunscreenType: sunscreenName
      });
      
      // Refill slot 1
      console.log(`Refilling slot 1 with: FULL capacity of ${sunscreenName}`);
      await api.device.registerRefill(blockSerial, {
        volumeML: 'FULL',
        slotIndex: 1,
        sunscreenType: sunscreenName
      });
      
      setSuccessMessage(`Both slots refilled for Block ${blockSerial} with ${sunscreenName}`);
      
      // OPTIMIZATION: Don't refresh all blocks after refill - let polling handle it
      // Only refresh if this is a manual user action that needs immediate feedback
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err) {
      console.error('Error during refill sequence:', err);
      setError(`Refill failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSlot = async (blockSerial, slotIndex) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use the new slot management API to toggle slot
      const result = await api.device.manageSlots(blockSerial, 'TOGGLE', { slotIndex });
      
      if (result.success) {
        setSuccessMessage(`Slot ${slotIndex + 1} toggled for Block ${blockSerial}`);
        
        // Refresh blocks data
        await refreshBlocks();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(result.error || 'Failed to toggle slot');
      }
      
    } catch (err) {
      console.error('Error toggling slot:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiagnostics = async (blockSerial) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await api.device.runDiagnostics(blockSerial);
      
      if (result.success) {
        setSuccessMessage(`Diagnostics started for Block ${blockSerial}`);
        
        // Refresh blocks data after diagnostics
        setTimeout(async () => {
          await refreshBlocks();
        }, 2000);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(result.error || 'Diagnostics failed');
      }
      
    } catch (err) {
      console.error('Error running diagnostics:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateBlockLocation = async (blockId, newLocation) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await api.block.updateBlockLocation(blockId, newLocation);
      
      if (result.success) {
        setSuccessMessage(`Block location updated successfully`);
        // Refresh blocks to show updated location
        refreshBlocks();
      } else {
        throw new Error(result.error || 'Failed to update location');
      }
      
    } catch (err) {
      console.error('Error updating block location:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUploaded = async (blockSerial, imageUrl) => {
    try {
      // Update the local blocks state to reflect the new image
      setBlocks(prevBlocks => 
        prevBlocks.map(block => 
          block.serial === blockSerial 
            ? { ...block, image: imageUrl, images: { ...block.images, profile: imageUrl } }
            : block
        )
      );
      
      setSuccessMessage('Block image updated successfully');
      
      // Refresh block data to ensure we have the latest from backend
      // This ensures images persist through page refreshes and UI state changes
      setTimeout(() => {
        fetchInitialBlocks();
      }, 1000); // Small delay to ensure backend has processed the update
      
    } catch (err) {
      console.error('Error handling image upload:', err);
      setError('Failed to update block image');
    }
  };

  const handleImageDeleted = async (blockSerial) => {
    try {
      // Update the local blocks state to reflect the removed image
      setBlocks(prevBlocks => 
        prevBlocks.map(block => 
          block.serial === blockSerial 
            ? { ...block, image: null, images: { ...block.images, profile: null } }
            : block
        )
      );
      
      setSuccessMessage('Block image deleted successfully');
    } catch (err) {
      console.error('Error handling image deletion:', err);
      setError('Failed to delete block image');
    }
  };

  const handleUpdateBlockAddress = async (blockId, newAddress) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await api.block.updateBlockAddress(blockId, newAddress);
      
      if (result.success) {
        setSuccessMessage(`Block address updated successfully`);
        // Refresh blocks to show updated address
        refreshBlocks();
      } else {
        throw new Error(result.error || 'Failed to update address');
      }
      
    } catch (err) {
      console.error('Error updating block address:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (blockId, imageFile) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Convert file to base64
      const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };
      
      const base64Data = await convertToBase64(imageFile);
      
      const result = await api.block.updateBlockImage(blockId, base64Data);
      
      if (result.success) {
        setSuccessMessage(`Block image uploaded successfully`);
        // Refresh blocks to show updated image
        refreshBlocks();
      } else {
        throw new Error(result.error || 'Failed to upload image');
      }
      
    } catch (err) {
      console.error('Error uploading block image:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogMaintenance = async (blockId, maintenanceData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await api.block.logMaintenance(blockId, maintenanceData.type, maintenanceData.notes);
      
      if (result.success) {
        setSuccessMessage(`Maintenance logged successfully`);
        // Refresh blocks to show updated maintenance data
        refreshBlocks();
      } else {
        throw new Error(result.error || 'Failed to log maintenance');
      }
      
    } catch (err) {
      console.error('Error logging maintenance:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceAction = async (serial, action, actionData = {}) => {
    console.log('🚀 BlockManagement.handleDeviceAction CALLED', { serial, action, actionData });
    try {
      setIsLoading(true);
      setError(null);
      
      let result;
      
      // Route to the correct API endpoint based on action
      switch (action) {
        case 'prime':
          result = await api.device.motorControl(serial, 'START');
          break;
        case 'paymentConfirmed':
          // Use new paymentData format (payment_id|dispenses_granted|price_paid) if available
          if (actionData.paymentData) {
            result = await api.device.paymentConfirmed(serial, actionData.paymentData);
          } else {
            // Legacy fallback for backward compatibility
            result = await api.device.paymentConfirmed(serial, actionData.dispenseCount || 1);
          }
          break;
        case 'setMode':
          result = await api.device.setMode(serial, actionData.mode);
          break;
        case 'clearErrors':
          result = await api.device.clearErrors(serial);
          break;
        case 'reset':
          result = await api.device.resetDevice(serial);
          break;
        case 'runDiagnostics':
          result = await api.device.runDiagnostics(serial);
          break;
        case 'getDeviceStatus':
          result = await api.device.getDeviceStatus(serial);
          break;
        case 'calibrateProximity':
          // Use sensorControl API for proximity calibration
          result = await api.device.sensorControl(serial, 'calibrateProximity', actionData);
          break;
        case 'motorControl':
          // Handle motor control commands
          result = await api.device.motorControl(serial, actionData.motorCommand, actionData.value);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      if (result.success) {
        setSuccessMessage(`Device action "${action}" completed successfully`);
        
        // OPTIMIZATION: Only refresh for actions that need immediate UI update
        const actionsNeedingRefresh = ['setMode', 'clearErrors', 'reset'];
        if (actionsNeedingRefresh.includes(action)) {
          console.log(`🔄 Refreshing blocks after ${action} action`);
          refreshBlocks();
        } else {
          console.log(`⏭️ Skipping refresh for ${action} - will be updated by polling`);
        }
        
        // Return the response code if available, otherwise return 0 for success
        const returnValue = result.responseCode !== undefined ? result.responseCode : 0;
        console.log('🎯 handleDeviceAction returning:', returnValue);
        return returnValue;
      } else {
        // If there's an error code, throw it so the notification system can display it
        const errorCode = result.errorCode !== undefined ? result.errorCode : -1;
        throw { message: result.error || 'Failed to perform device action', errorCode };
      }
      
    } catch (err) {
      console.error('Error performing device action:', err);
      
      // Re-throw the error with error code if it has one
      if (err.errorCode !== undefined) {
        throw err;
      }
      
      // Handle specific error types with user-friendly feedback
      if (err.type === 'DEVICE_OFFLINE') {
        // Device is offline - provide clear feedback and reset device state
        setError(`📱 Device ${serial} is currently offline. Please check the device connection and try again.`);
        
        // Update the device status in the blocks list to reflect offline state
        setBlocks(prevBlocks => prevBlocks.map(block => {
          if (block.serial === serial) {
            return {
              ...block,
              status: 'offline',
              lastSeen: new Date().toISOString(),
              connectionStatus: 'offline'
            };
          }
          return block;
        }));
        
        // Show a more detailed error message for prime action
        if (action === 'prime') {
          setError(`📱 Cannot prime device ${serial} - device is offline. Please check the device power and network connection, then try again.`);
        }
        
      } else if (err.type === 'DEVICE_TIMEOUT') {
        setError(`⏱️ Device ${serial} is not responding. Please try again in a moment.`);
        throw { message: err.message, errorCode: -2 };
      } else {
        setError(err.message || 'Device operation failed');
        // Throw error with code for notification system
        throw { message: err.message || 'Device operation failed', errorCode: err.errorCode || -1 };
      }
    } finally {
      setIsLoading(false);
    }
  };

  const groupBlocksByLocation = (blocks) => {
    const grouped = {};
    blocks.forEach(block => {
      const location = block.location || 'Unknown Location';
      if (!grouped[location]) {
        grouped[location] = [];
      }
      grouped[location].push(block);
    });
    return grouped;
  };

  const getLocationStatus = (locationBlocks) => {
    // Use normalized status logic for consistent location status calculation
    const statuses = locationBlocks.map(block => {
      // Always use normalized status utility for consistency
      const normalized = getNormalizedBlockStatus(
        block, 
        block.activeErrors || block.errors || [], 
        block.activeWarnings || block.warnings || []
      );
      return normalized.status;
    });

    // Return worst status (critical > warning > healthy)
    if (statuses.includes('critical')) return 'critical';
    if (statuses.includes('warning')) return 'warning';
    return 'healthy';
  };

  // Fetch initial blocks on component mount
  useEffect(() => {
    fetchInitialBlocks();
    fetchSunscreenTypes();
  }, [fetchInitialBlocks]);

  // Confirmation modal helper functions

  const hideConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      actionType: 'delete',
      itemName: '',
      onConfirm: null
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    hideConfirmationModal();
  };

  const groupedBlocks = groupBlocksByLocation(blocksWithCanonicalStatus);

  if (loading && blocks.length === 0) {
    return (
      <div className="block-management">
        <LoadingSpinner message="Loading block management system..." size="large" />
      </div>
    );
  }

  return (
    <div className="block-management">
      {/* Header Section */}
      <div className="section-header">
        <div className="header-content">
          <h1 className="section-title">Block Management</h1>
          <p className="section-subtitle">
            Monitor and manage your sunscreen dispensing devices
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-outline"
            onClick={() => refreshBlocks(true)}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FiRefreshCw className="spinning" />
                Refreshing...
              </>
            ) : (
              <>
                <FiRefreshCw />
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success">
          <div className="alert-icon">✅</div>
          <div className="alert-content">
            <div className="alert-message">{successMessage}</div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger">
          <div className="alert-icon">❌</div>
          <div className="alert-content">
            <div className="alert-message">{error}</div>
          </div>
        </div>
      )}

      {/* Health Status Cards */}
      <div className="block-section">
        {/* Data processed and ready for HealthStatusCards component */}
        <HealthStatusCards blocks={blocksWithCanonicalStatus} />
      </div>

      {/* Blocks by Location */}
      <div className="block-section">
        <div className="locations-container">
          {Object.entries(groupedBlocks).map(([location, locationBlocks]) => (
            <div key={location} className="location-section">
              <div 
                className="location-section-header"
                onClick={() => toggleLocation(location)}
              >
                <h2 className="location-section-title">
                  <span className={`location-status-icon ${getLocationStatus(locationBlocks)}`}>
                    {getLocationStatus(locationBlocks) === 'critical' ? '🔴' : 
                     getLocationStatus(locationBlocks) === 'warning' ? '🟡' : '🟢'}
                  </span>
                  <span className="location-name-text">{location}</span>
                  <FiChevronRight className={`location-chevron ${expandedLocations[location] ? 'expanded' : ''}`} />
                </h2>
                <div className="location-summary">
                  <span className="location-count">{locationBlocks.length} devices</span>
                  <span className="location-status">
                    {locationBlocks.filter(b => b.is_online).length} online
                  </span>
                </div>
              </div>
              
              {expandedLocations[location] && (
                <div className="location-section-body">
                  <div className="location-blocks-container">
                    {locationBlocks.map((block) => (
                      <div key={block.serial} className="full-width-block-wrapper">
                        <div 
                          className={`block-card-wrapper ${expandedBlocks[block.serial] ? 'expanded' : ''}`}
                          onClick={() => toggleBlock(block.serial)}
                        >
                          <div className="block-card-header">
                            <div className="block-card-image-section">
                              <ImageUpload
                                blockSerial={block.serial}
                                currentImageUrl={block.image || block.images?.profile}
                                onImageUploaded={(imageUrl) => handleImageUploaded(block.serial, imageUrl)}
                                onImageDeleted={() => handleImageDeleted(block.serial)}
                                imageType="profile"
                              />
                            </div>
                            <div className="block-card-title-section">
                              <h3 className="block-card-title">{block.name || `Block ${block.serial}`}</h3>
                              <p className="block-card-subtitle">{block.address}</p>
                            </div>
                            
                            {/* Block Stats Preview */}
                            <div className="block-stats-preview">
                              <div className="stats-row">
                                <div className="stat-item-compact">
                                  <FiRss className="stat-icon-compact" />
                                  <span className="stat-value-compact">
                                    {(() => {
                                      // Use the same normalized status logic as the badge for consistency
                                      const normalizedStatus = getNormalizedBlockStatus(block, block.activeErrors || block.errors || [], block.activeWarnings || block.warnings || []);
                                      const isOffline = normalizedStatus.status === 'critical' && 
                                        (normalizedStatus.reason.includes('offline') || normalizedStatus.reason.includes('heartbeat'));
                                      
                                      if (isOffline) {
                                        const lastHeartbeat = block?.lastHeartbeat || block?.last_heartbeat;
                                        if (lastHeartbeat) {
                                          const date = new Date(lastHeartbeat);
                                          const now = new Date();
                                          const diffMs = now - date;
                                          const diffMins = Math.floor(diffMs / (1000 * 60));
                                          if (diffMins < 60) {
                                            return `${diffMins}m ago`;
                                          } else if (diffMins < 1440) {
                                            return `${Math.floor(diffMins / 60)}h ago`;
                                          } else {
                                            return `${Math.floor(diffMins / 1440)}d ago`;
                                          }
                                        }
                                        return 'Offline';
                                      }
                                      return 'Online';
                                    })()} 
                                  </span>
                                  <span className="stat-label-compact">
                                    {(() => {
                                      const normalizedStatus = getNormalizedBlockStatus(block, block.activeErrors || block.errors || [], block.activeWarnings || block.warnings || []);
                                      const isOffline = normalizedStatus.status === 'critical' && 
                                        (normalizedStatus.reason.includes('offline') || normalizedStatus.reason.includes('heartbeat'));
                                      return isOffline ? 'Last Seen' : 'Status';
                                    })()} 
                                  </span>
                                </div>
                                <div className="stat-item-compact">
                                  <FiDollarSign className="stat-icon-compact" />
                                  <span className="stat-value-compact">
                                    ${calculateDailyRevenue(block).toFixed(2)}
                                  </span>
                                  <span className="stat-label-compact">Today</span>
                                </div>
                                {/* NEW: Multi-slot sunscreen display */}
                                <div className="slot-info-compact">
                                  <div className="slot-item-compact">
                                    <FiDroplet className="stat-icon-compact slot1" />
                                    <div className="slot-details-compact">
                                      <span className="slot-value-compact">
                                        {block.slot1_percent !== undefined ? `${Math.round(block.slot1_percent)}%` : 'N/A'}
                                      </span>
                                      <span className="slot-label-compact">
                                        Slot 1: {block.slot1_type || 'Empty'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="slot-item-compact">
                                    <FiDroplet className="stat-icon-compact slot2" />
                                    <div className="slot-details-compact">
                                      <span className="slot-value-compact">
                                        {block.slot2_percent !== undefined ? `${Math.round(block.slot2_percent)}%` : 'N/A'}
                                      </span>
                                      <span className="slot-label-compact">
                                        Slot 2: {block.slot2_type || 'Empty'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="stat-item-compact">
                                  <FiBattery className="stat-icon-compact" />
                                  <span className="stat-value-compact">
                                    {block.battery_soc !== undefined ? `${Math.round(block.battery_soc)}%` : 'N/A'}
                                  </span>
                                  <span className="stat-label-compact">Battery</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Status badge with emoji indicator */}
                            <div className="block-card-status">
                              {(() => {
                                // Use normalized status logic (handles offline as critical automatically)
                                const normalizedStatus = getNormalizedBlockStatus(block, block.activeErrors || block.errors || [], block.activeWarnings || block.warnings || []);
                                const statusEmoji = normalizedStatus.status === 'critical' ? '🔴' : normalizedStatus.status === 'warning' ? '🟡' : '🟢';
                                return (
                                  <StatusTooltip
                                    status={normalizedStatus.status}
                                    errors={block.activeErrors || block.errors || []}
                                    warnings={block.activeWarnings || block.warnings || []}
                                    position="top"
                                  >
                                    <span className={`block-status-badge ${normalizedStatus.status}`}>{statusEmoji}</span>
                                  </StatusTooltip>
                                );
                              })()}
                              <FiChevronDown className={`block-chevron ${expandedBlocks[block.serial] ? 'expanded' : ''}`} />
                            </div>
                          </div>

                        </div>
                        
                        {expandedBlocks[block.serial] && (
                          <BlockDetailsView
                            block={block}
                            isExpanded={expandedBlocks[block.serial]}
                            onToggle={toggleBlock}
                            onUpdateLocation={handleUpdateBlockLocation}
                            onUpdateAddress={handleUpdateBlockAddress}
                            onImageUpload={handleImageUpload}
                            onLogMaintenance={handleLogMaintenance}
                            onDeviceAction={handleDeviceAction}
                            userRole={userRole}
                            isAdmin={isAdmin}
                            isFranchisee={isFranchisee}
                            isOperator={isOperator}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Expanded Block Details */}
      {selectedBlock && (
        <div className="block-section">
          <div className="block-detail-view">
            <div className="block-detail-header">
              <h2 className="block-detail-title">
                Block {selectedBlock} - Detailed View
              </h2>
              <button 
                className="btn btn-ghost"
                onClick={() => setSelectedBlock(null)}
              >
                <span className="btn-icon">×</span>
                Close
              </button>
            </div>
            
            <SlotManager
              slots={blocks.find(b => b.serial === selectedBlock)?.slots || []}
              deviceSerial={selectedBlock}
              sunscreenTypes={sunscreenTypes}
              onRefillSlot={(slotIndex, refillData) => 
                handleRefillSlot(selectedBlock, slotIndex, refillData)
              }
              onToggleSlot={(slotIndex) => 
                handleToggleSlot(selectedBlock, slotIndex)
              }
              isLoading={isLoading}
            />
            
            {/* Additional Management Tools */}
            <div className="management-tools">
              <h3 className="management-tools-title">Device Management</h3>
              <div className="management-tools-grid">
                <button 
                  className="management-tool-btn diagnostics"
                  onClick={() => handleDiagnostics(selectedBlock)}
                  disabled={isLoading}
                >
                  <span className="btn-icon">🔍</span>
                  <span className="btn-text">Run Diagnostics</span>
                </button>
                <button 
                  className="management-tool-btn maintenance"
                  onClick={() => {/* Handle maintenance mode */}}
                  disabled={isLoading}
                >
                  <span className="btn-icon">🔧</span>
                  <span className="btn-text">Maintenance Mode</span>
                </button>
                <button 
                  className="management-tool-btn settings"
                  onClick={() => {/* Handle device settings */}}
                  disabled={isLoading}
                >
                  <span className="btn-icon">⚙️</span>
                  <span className="btn-text">Device Settings</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmationModal.isOpen && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          title={confirmationModal.title}
          message={confirmationModal.message}
          actionType={confirmationModal.actionType}
          itemName={confirmationModal.itemName}
          onConfirm={handleConfirmAction}
          onCancel={hideConfirmationModal}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default BlockManagement;
